import { useGraphMutation, useToast } from "@dashboard/library";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SaveCustomSummaryResultMutationVariables } from "@/graphql/generated/graphql";
import { SaveCustomSummaryResult } from "@/graphql/mutations/SaveCustomSummaryResult";
import { getErrorMessageFromBackendError } from "@/helpers/graphql/graphql.helpers";

export function useSaveCustomSummaryResult() {
  const { t } = useTranslation();
  const { show } = useToast();
  const [mutate, { loading }] = useGraphMutation(SaveCustomSummaryResult);

  const saveCustomSummaryResult = useCallback(
    async (variables: SaveCustomSummaryResultMutationVariables) => {
      const { error, exceptions } = await mutate({
        refetchQueries: ["GetCustomSummaryResults"],
        variables,
      });

      if (error) {
        show({
          message: t("error.generic.title"),
          variant: "critical",
        });
        return t(error);
      }

      const exception =
        exceptions?.GenericException ||
        exceptions?.UserInputException ||
        exceptions?.CustomSummaryException ||
        exceptions?.InsufficientLicenseException;

      if (exception) {
        const errorMessage = getErrorMessageFromBackendError(exception);
        show({
          message: errorMessage,
          variant: "critical",
        });
        return errorMessage;
      }

      show({
        message: t("custom_summary.custom_summary_updated"),
        variant: "success",
      });
      return undefined;
    },
    [t, mutate, show]
  );

  return {
    saveCustomSummaryResult,
    loading,
  };
}
